<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';

import { reactive, ref } from 'vue';

import KTextField from '@/components/fields/KTextField.vue';
import PhoneInput from '@/components/inputs/PhoneInput.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

export interface InitialData {
  caller?: string;
  phone?: string;
  reference_1?: string;
  reference_2?: string;
  reference_3?: string;
  reference_4?: string;
}

export interface ValidData {
  caller: string;
  phone: string;
  reference_1?: string;
  reference_2?: string;
  reference_3?: string;
  reference_4?: string;
}

export interface Props {
  readonly?: boolean;
  initialData?: InitialData;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  initialData: () => ({}),
});

const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

// reference_1..4 are populated by useForm's deep watcher from props.initialData
const formData = reactive<ValidData>({ caller: '', phone: '' });
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, function (event) {
  emit('submit', event, formData);
});

defineExpose({
  form,
});
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    :class="[$style.jobEditCustomerForm, { [$style.editing]: !props.readonly }]"
    validate-on="submit lazy"
    data-testid="jobEditCustomerForm"
    @submit.prevent="handleSubmit"
  >
    <div :class="$style.formSection">
      <div :class="$style.row">
        <KTextField
          v-model="formData.caller"
          label="Caller"
          size="lg"
          :rules="[requiredValidationRule]"
          :readonly="props.readonly"
          validate-on="input"
          :class="$style.field"
        />
        <PhoneInput
          v-model="formData.phone"
          size="lg"
          :readonly="props.readonly"
          :class="$style.field"
        />
        <KTextField
          v-model="formData.reference_1"
          label="Reference"
          size="lg"
          :readonly="props.readonly"
          :class="$style.field"
        />
      </div>

      <div :class="$style.row">
        <KTextField
          v-model="formData.reference_2"
          label="Reference 2"
          size="lg"
          :readonly="props.readonly"
          :class="$style.field"
        />
        <KTextField
          v-model="formData.reference_3"
          label="Reference 3"
          size="lg"
          :readonly="props.readonly"
          :class="$style.field"
        />
        <KTextField
          v-model="formData.reference_4"
          label="Reference 4"
          size="lg"
          :readonly="props.readonly"
          :class="$style.field"
        />
      </div>
    </div>
    <!-- :disabled, not :readonly: submit input has no readonly state -->
    <input type="submit" class="hidden" :disabled="props.readonly" />
  </v-form>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobEditCustomerForm {
  width: 100%;
}

.formSection {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.field {
  width: 100%;
}
</style>
